// src/pages/admin/HomeNoticeSettings.tsx (통째 교체용 최종본)
import { useEffect, useMemo, useState, ChangeEvent } from "react";
import { authFetch } from "../../lib/auth";

type State = {
  loading: boolean;
  uploading: boolean;

  // 이미지(미리보기용)
  imageUrl: string | null;

  // 토글
  enabled: boolean;
  toggleSaving: boolean;

  // 에러 표시
  error: string | null;
};

function pick(obj: any, keys: string[]) {
  for (const k of keys) {
    if (!obj) continue;
    if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
}

function parseEnabled(data: any): boolean {
  // 가능한 형태 모두 수용:
  // { ok:true, enabled:true }
  // { ok:true, popup:{ enabled:true } }
  // { ok:true, row:{ enabled:true } }
  // { ok:true, home_popup:{ enabled:true } }
  const direct = pick(data, ["enabled"]);
  if (typeof direct === "boolean") return direct;

  const nested =
    pick(data?.popup, ["enabled"]) ??
    pick(data?.row, ["enabled"]) ??
    pick(data?.home_popup, ["enabled"]) ??
    pick(data?.data, ["enabled"]);
  return !!nested;
}

export default function HomeNoticeSettings() {
  const [state, setState] = useState<State>({
    loading: true,
    uploading: false,
    imageUrl: null,
    enabled: false,
    toggleSaving: false,
    error: null,
  });

  const toggleLabel = useMemo(() => (state.enabled ? "ON" : "OFF"), [state.enabled]);

  // 최초 로딩: (1) 토글 상태(관리자) (2) 현재 이미지(공개)
  useEffect(() => {
    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, error: null }));

        const [popupRes, noticeRes] = await Promise.all([
          authFetch("/api/admin/home-popup"),
          fetch("/api/public/home-notice", { cache: "no-store" }),
        ]);

        // authFetch는 보통 401/403도 응답을 주니 여기서 처리
        const popupData = await popupRes.json().catch(() => null);
        const noticeData = await noticeRes.json().catch(() => null);

        if (!popupRes.ok || !popupData?.ok) {
          const msg =
            popupData?.message ||
            popupData?.error ||
            `토글 상태 조회 실패 (HTTP ${popupRes.status})`;
          setState((s) => ({ ...s, loading: false, error: msg }));
          return;
        }

        const enabled = parseEnabled(popupData);
        const imageUrl = noticeData?.imageUrl ?? null;

        setState((s) => ({
          ...s,
          loading: false,
          enabled,
          imageUrl,
          error: null,
        }));
      } catch (e: any) {
        console.error("load home notice settings error", e);
        setState((s) => ({
          ...s,
          loading: false,
          error: "초기 로딩 중 오류가 발생했습니다.",
        }));
      }
    })();
  }, []);

  const saveEnabled = async (nextEnabled: boolean) => {
    // 낙관적 업데이트 + 실패 시 롤백
    const prev = state.enabled;

    setState((s) => ({
      ...s,
      enabled: nextEnabled,
      toggleSaving: true,
      error: null,
    }));

    try {
      const res = await authFetch("/api/admin/home-popup", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        const msg =
          data?.message || data?.error || `저장 실패 (HTTP ${res.status})`;
        alert(msg);

        // 롤백
        setState((s) => ({
          ...s,
          enabled: prev,
          toggleSaving: false,
          error: msg,
        }));
        return;
      }

      const enabledFromServer = parseEnabled(data);

      setState((s) => ({
        ...s,
        enabled: enabledFromServer,
        toggleSaving: false,
        error: null,
      }));
    } catch (e) {
      console.error("toggle home-popup error", e);
      alert("토글 저장 중 오류가 발생했습니다.");

      // 롤백
      setState((s) => ({
        ...s,
        enabled: prev,
        toggleSaving: false,
        error: "토글 저장 중 오류가 발생했습니다.",
      }));
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const form = new FormData();
    form.append("image", file);

    setState((s) => ({ ...s, uploading: true, error: null }));
    try {
      const res = await authFetch("/api/admin/home-notice/image", {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        const msg =
          data?.message || data?.error || `업로드 실패 (HTTP ${res.status})`;
        alert(msg);
        setState((s) => ({ ...s, uploading: false, error: msg }));
        return;
      }

      setState((s) => ({
        ...s,
        uploading: false,
        imageUrl: data.imageUrl ?? null,
        error: null,
      }));
    } catch (e) {
      console.error("upload home notice error", e);
      alert("업로드 중 오류가 발생했습니다.");
      setState((s) => ({
        ...s,
        uploading: false,
        error: "업로드 중 오류가 발생했습니다.",
      }));
    } finally {
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("현재 홈 팝업 공지 이미지를 삭제하시겠습니까?")) return;

    try {
      const res = await authFetch("/api/admin/home-notice/image", {
        method: "DELETE",
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        const msg =
          data?.message || data?.error || `삭제 실패 (HTTP ${res.status})`;
        alert(msg);
        setState((s) => ({ ...s, error: msg }));
        return;
      }

      setState((s) => ({ ...s, imageUrl: null, error: null }));
    } catch (e) {
      console.error("delete home notice error", e);
      alert("삭제 중 오류가 발생했습니다.");
      setState((s) => ({ ...s, error: "삭제 중 오류가 발생했습니다." }));
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">홈 팝업 공지</h1>
          <p className="mt-1 text-sm text-slate-600">
            PC에서 홈페이지 접속 시 새 창으로 띄워지는 공지 팝업 이미지를 관리합니다.
            <br />
            이미지를 등록해도 <b>토글이 OFF</b>면 팝업이 표시되지 않습니다.
          </p>

          {state.error && (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {state.error}
            </div>
          )}
        </div>

        {/* 토글 */}
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-slate-900">팝업 노출</div>

            {state.loading ? (
              <span className="text-xs text-slate-400">불러오는 중...</span>
            ) : (
              <button
                type="button"
                onClick={() => saveEnabled(!state.enabled)}
                disabled={state.toggleSaving}
                className={[
                  "relative inline-flex h-7 w-12 items-center rounded-full transition",
                  state.enabled ? "bg-emerald-500" : "bg-slate-300",
                  state.toggleSaving ? "opacity-60" : "",
                ].join(" ")}
                aria-label="홈 팝업 토글"
                title={toggleLabel}
              >
                <span
                  className={[
                    "inline-block h-5 w-5 transform rounded-full bg-white transition",
                    state.enabled ? "translate-x-6" : "translate-x-1",
                  ].join(" ")}
                />
              </button>
            )}

            <span
              className={[
                "text-xs font-semibold",
                state.enabled ? "text-emerald-700" : "text-slate-500",
              ].join(" ")}
            >
              {toggleLabel}
            </span>

            {state.toggleSaving && (
              <span className="text-[11px] text-slate-400">저장중...</span>
            )}
          </div>

          {!state.loading && state.enabled && !state.imageUrl && (
            <div className="mt-2 text-[11px] text-amber-700">
              ⚠️ 토글은 ON인데 이미지가 없습니다. 이미지 업로드 후 확인하세요.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {/* 현재 이미지 */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-900">현재 등록된 이미지</div>

            {!state.loading && state.imageUrl && (
              <a
                href={state.imageUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-600 hover:underline"
              >
                원본 보기
              </a>
            )}
          </div>

          {state.loading ? (
            <div className="flex h-40 items-center justify-center rounded-xl bg-slate-50 text-xs text-slate-400">
              불러오는 중...
            </div>
          ) : state.imageUrl ? (
            <div className="space-y-2">
              <div className="flex justify-center">
                <img
                  src={state.imageUrl}
                  alt="홈 팝업 공지 이미지"
                  className="max-h-[480px] w-auto rounded-xl border border-slate-200 bg-slate-50 object-contain"
                />
              </div>

              <div className="text-[11px] text-slate-500">
                실제 팝업 창 크기에 맞춰 자동으로 축소/확대되어 표시됩니다.
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-xs text-red-600 hover:underline"
                >
                  이미지 삭제
                </button>

                {!state.enabled && (
                  <span className="text-[11px] text-slate-500">
                    (현재 토글 OFF 상태라 사용자에게는 팝업이 뜨지 않습니다)
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center rounded-xl bg-slate-50 text-xs text-slate-400">
              등록된 이미지가 없습니다. 아래에서 새 이미지를 업로드해 주세요.
            </div>
          )}
        </div>

        {/* 업로드 */}
        <div className="mt-6 border-t border-slate-100 pt-4">
          <div className="mb-2 text-sm font-medium text-slate-900">이미지 업로드</div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700">
              {state.uploading ? "업로드 중..." : "이미지 선택"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                disabled={state.uploading}
              />
            </label>
            <span className="text-[11px] text-slate-500">
              권장: 세로형 이미지 (예: 600×800px 이상), JPG/PNG, 최대 5MB
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
